import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import type {
  AnalyticsGranularity,
  AnalyticsRange,
} from "../../stats/api/statsApi";
import {
  useAnalyticsOverviewQuery,
  useEventCountsQuery,
} from "../../stats/hooks/useStats";

interface FunnelStep {
  key: string;
  label: string;
  count: number;
}

interface FunnelViewProps {
  range: AnalyticsRange;
  granularity: AnalyticsGranularity;
}

const formatPct = (value: number) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : "—";

export const FunnelView = ({ range, granularity }: FunnelViewProps) => {
  const { t } = useTranslation("common");
  const overviewQuery = useAnalyticsOverviewQuery(range, granularity);
  const eventsQuery = useEventCountsQuery(range);

  const steps = useMemo<FunnelStep[]>(() => {
    const overview = overviewQuery.data;
    const events = eventsQuery.data ?? [];
    const lookup = new Map(events.map((e) => [e.name, e.count]));
    return [
      {
        key: "visitors",
        label: t("FUNNEL.steps.visitors"),
        count: overview?.cards.activeUsers ?? 0,
      },
      {
        key: "download_started",
        label: t("FUNNEL.steps.download"),
        count: lookup.get("download_started") ?? 0,
      },
      {
        key: "signup_completed",
        label: t("FUNNEL.steps.signup"),
        count: lookup.get("signup_completed") ?? 0,
      },
      {
        key: "subscription_paid",
        label: t("FUNNEL.steps.subscription"),
        count: lookup.get("subscription_paid") ?? 0,
      },
    ];
  }, [overviewQuery.data, eventsQuery.data, t]);

  const isLoading = overviewQuery.isLoading || eventsQuery.isLoading;
  const isFetching = overviewQuery.isFetching || eventsQuery.isFetching;

  const top = steps[0]?.count ?? 0;

  return (
    <div className="relative space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            {t("FUNNEL.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))
            : steps.map((step, idx) => {
                const widthPct = top > 0 ? (step.count / top) * 100 : 0;
                const prev = idx > 0 ? steps[idx - 1].count : null;
                const stepRate =
                  prev != null && prev > 0 ? step.count / prev : null;
                const overallRate = top > 0 ? step.count / top : null;
                return (
                  <div key={step.key} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-neutral-100">
                        {step.label}
                      </span>
                      <span className="tabular-nums text-neutral-300">
                        {step.count.toLocaleString("es-AR")}
                        {stepRate != null && (
                          <span className="ml-2 text-xs text-neutral-500">
                            {formatPct(stepRate)} {t("FUNNEL.fromPrev")}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="relative h-8 w-full overflow-hidden rounded-md bg-neutral-800/60">
                      <div
                        className="h-full bg-gradient-to-r from-primary/60 to-primary/30 transition-[width] duration-500"
                        style={{ width: `${Math.max(widthPct, 2)}%` }}
                      />
                      {overallRate != null && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-neutral-400 tabular-nums">
                          {formatPct(overallRate)} {t("FUNNEL.fromTop")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">
            {t("FUNNEL.allEvents")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("FUNNEL.event")}</TableHead>
                <TableHead className="text-right">
                  {t("FUNNEL.count")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    <TableCell>
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-4 w-12" />
                    </TableCell>
                  </TableRow>
                ))
              ) : (eventsQuery.data?.length ?? 0) === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="text-center text-muted-foreground"
                  >
                    {t("FUNNEL.noEvents")}
                  </TableCell>
                </TableRow>
              ) : (
                eventsQuery.data?.map((event) => (
                  <TableRow key={event.name}>
                    <TableCell className="font-mono text-xs">
                      {event.name}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {event.count.toLocaleString("es-AR")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isFetching && !isLoading ? <LoadingOverlay /> : null}
    </div>
  );
};
